import React from 'react';
import { SubdomainLink } from './SubdomainLink';
import { Instagram, Facebook, Linkedin, Youtube } from 'lucide-react';
import { useToast } from '../hooks/useToast';

const Footer = () => {
  const { toast } = useToast();

  const handleSubscribe = (e) => {
    const emailInput = document.getElementById('EMBED_FORM_EMAIL_LABEL');
    if (!emailInput) {
      e.preventDefault();
      return;
    }

    const emailValue = emailInput.value.trim();
    if (!emailValue) {
      toast.warning('Please enter your email address.');
      e.preventDefault();
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
      toast.error('Please enter a valid email address.');
      e.preventDefault();
      return;
    }

    // Submission will proceed natively to the hidden iframe
    toast.success('Thank you for subscribing to our newsletter!');
    // Clear the input field after a micro-delay so the form submission has the value at the moment of submitting
    setTimeout(() => {
      emailInput.value = '';
    }, 100);
  };

  return (
    <footer className="text-white pt-12 pb-6" style={{ backgroundColor: '#060C22' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-10 mb-10">
          {/* Footer Logo - Specific File */}
          <div className="space-y-4">
            <SubdomainLink to="/">
              <img 
                src="/Top_Interior_India_2.png" 
                alt="Top Interiors India" 
                className="h-14 sm:h-16 md:h-20 w-auto object-contain"
              />
            </SubdomainLink>
            <p className="text-gray-300 text-[14px] leading-relaxed max-w-[280px]">
              India's fastest growing interior-architect industry – Where architect directly connect with vendors in one single click
            </p>
            <div className="flex space-x-6">
              <a href="https://www.instagram.com/topinteriorsindia" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors"><Instagram size={20} /></a>
              <a href="https://www.facebook.com/topinteriorsindia" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors"><Facebook size={20} /></a>
              <a href="https://www.linkedin.com/company/top-interiors-india" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors"><Linkedin size={20} /></a>
              <a href="https://www.youtube.com/@topinteriorsindia" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors"><Youtube size={20} /></a>
              <a href="https://x.com/topinteriorsin" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Trending Searches */}
          <div className="space-y-4">
            <h4 className="text-xl font-medium tracking-tight">Trending Searches</h4>
            <ul className="space-y-3 text-sm text-gray-300">
              <li><SubdomainLink to="/listing?searchName=Furniture" className="hover:text-primary transition-colors">Furniture</SubdomainLink></li>
              <li><SubdomainLink to="/listing?searchName=Lighting" className="hover:text-primary transition-colors">Lighting</SubdomainLink></li>
              <li><SubdomainLink to="/listing?searchName=Door & Frame" className="hover:text-primary transition-colors">Door & Frame</SubdomainLink></li>
              <li><SubdomainLink to="/listing?searchName=Plywood Supplier" className="hover:text-primary transition-colors">Plywood Supplier</SubdomainLink></li>
              <li><SubdomainLink to="/listing?searchName=Home Automation" className="hover:text-primary transition-colors">Home Automation</SubdomainLink></li>
            </ul>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-xl font-medium tracking-tight">Quick Links</h4>
            <ul className="space-y-3 text-sm text-gray-300">
              <li><SubdomainLink to="/about-us" className="hover:text-primary transition-colors">About Us</SubdomainLink></li>
              <li><SubdomainLink to="/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</SubdomainLink></li>
              <li><SubdomainLink to="/terms-conditions" className="hover:text-primary transition-colors">Terms & Conditions</SubdomainLink></li>
              <li><SubdomainLink to="/refund-cancellation-policy" className="hover:text-primary transition-colors">Refund & Cancellation Policy</SubdomainLink></li>
            </ul>
          </div>

          {/* Newsletter (Zoho Campaigns Web-Optin Form) */}
          <div className="space-y-4">
            <h4 className="text-xl font-medium tracking-tight">Newsletter</h4>
            <p className="text-gray-300 text-sm leading-relaxed">
              Want to stay up to date with TII? Please Subscribe
            </p>
            
            <div id="sf3z7211b21830d71c2ad449594eef9b3eb224ce6314dee988b7de6678e835d682d4" data-type="signupform" style={{ opacity: 1 }}>
              <div id="customForm">
                <div className="quick_form_9_css" style={{ backgroundColor: 'rgba(0, 0, 0, 0)', zIndex: 2, overflow: 'hidden' }} name="SIGNUP_BODY">
                  <div>
                    <form 
                      method="POST" 
                      id="zcampaignOptinForm" 
                      style={{ margin: '0px', width: '100%', padding: '0px' }} 
                      action="https://zgp4-zgp4.maillist-manage.in/weboptin.zc" 
                      target="zoho_campaigns_iframe"
                      onSubmit={handleSubscribe}
                    >
                      <div className="flex bg-white rounded-md overflow-hidden max-w-[280px] border border-gray-200">
                        {/* Email Input Field */}
                        <input 
                          type="text" 
                          placeholder="Enter your email" 
                          changeitem="SIGNUP_FORM_FIELD" 
                          name="CONTACT_EMAIL" 
                          id="EMBED_FORM_EMAIL_LABEL"
                          className="flex-1 px-4 py-3 text-secondary text-sm focus:outline-none bg-white min-w-0"
                        />
                        
                        {/* Submit button */}
                        <button 
                          type="submit"
                          name="SIGNUP_SUBMIT_BUTTON" 
                          className="bg-primary hover:bg-[#23977a] text-white px-4 py-3 text-sm font-semibold transition-colors cursor-pointer border-none outline-none"
                        >
                          Subscribe
                        </button>
                      </div>

                      {/* Zoho Required Fields */}
                      <input type="hidden" id="fieldBorder" value="" />
                      <input type="hidden" id="submitType" name="submitType" value="optinCustomView" />
                      <input type="hidden" id="emailReportId" name="emailReportId" value="" />
                      <input type="hidden" id="formType" name="formType" value="QuickForm" />
                      <input type="hidden" name="zx" id="cmpZuid" value="1dfc3cc24e" />
                      <input type="hidden" name="zcvers" value="2.0" />
                      <input type="hidden" name="oldListIds" id="allCheckedListIds" value="" />
                      <input type="hidden" id="mode" name="mode" value="OptinCreateView" />
                      <input type="hidden" id="zcld" name="zcld" value="14b758dc472e2f8d" />
                      <input type="hidden" id="zctd" name="zctd" value="14b758dc472e2e31" />
                      <input type="hidden" id="document_domain" value="" />
                      <input type="hidden" id="zc_Url" value="zgp4-zgp4.maillist-manage.in" />
                      <input type="hidden" id="new_optin_response_in" value="0" />
                      <input type="hidden" id="duplicate_optin_response_in" value="0" />
                      <input type="hidden" name="zc_trackCode" id="zc_trackCode" value="ZCFORMVIEW" />
                      <input type="hidden" id="zc_formIx" name="zc_formIx" value="3z7211b21830d71c2ad449594eef9b3eb224ce6314dee988b7de6678e835d682d4" />
                      <input type="hidden" id="viewFrom" value="URL_ACTION" />
                      <span style={{ display: 'none' }} id="dt_CONTACT_EMAIL">1,true,6,Contact Email,2</span>
                    </form>
                  </div>
                </div>
              </div>
            </div>

            {/* Hidden iframe for background submission */}
            <iframe 
              name="zoho_campaigns_iframe" 
              id="zoho_campaigns_iframe" 
              style={{ display: 'none' }}
              title="Zoho Campaigns Subscription Frame"
            ></iframe>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-4 text-center">
          <p className="text-sm text-gray-400">
            © Top Interiors India 2026 | All Right Reserved
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
